import { DATA_SOURCES } from '@/config/constants';
import EntityCreators from '../shared/entity/EntityCreators';
import EntityHeroCard from '../shared/entity/EntityHeroCard';
import EntityPage from '../shared/entity/EntityPage';
import EntityRelatedTable from '../shared/entity/EntityRelatedTable';
import EntityTabs, { EntityTab } from '../shared/entity/EntityTabs';
import EntityTopPosts from '../shared/entity/EntityTopPosts';
import EntityTopTerms from '../shared/entity/EntityTopTerms';
import { GOOGLE_METADATA_BY_KEY, TIKTOK_METADATA_BY_KEY } from 'utils/metrics';
import { getTikTokAndSearchHealthStats } from 'state/utils';
import { RelatedCategoriesSectionTable } from 'components/DataVis/RelatedCategoriesTable';
import { TikTokChartCard } from '../shared/entity/chartCards';
import ToggleWatch from 'components/Button/ToggleWatch';
import { TopHashtagsSection } from 'components/TopKeywordsSection';
import { TopPostsSection } from 'components/Posts';
import { useSelector } from 'react-redux';
import useTikTokEntity from '../shared/entity/useTikTokEntity';
import { useTrackBrandView } from 'utils/hooks/useTrackEntityView';

const METRICS = [
    TIKTOK_METADATA_BY_KEY.engagement_prc,
    TIKTOK_METADATA_BY_KEY.sentiment_score,
    TIKTOK_METADATA_BY_KEY.total_posts_cnt,
    TIKTOK_METADATA_BY_KEY.partner_paid_partnership_posts_views_prc,
    TIKTOK_METADATA_BY_KEY.eligible_for_commission_posts_views_prc,
    GOOGLE_METADATA_BY_KEY.price_segmentation,
];

const HEALTH_STAT_KEYS = [
    'average weekly views',
    'price point',
    'paid views',
    'sentiment score',
    'posts',
    'engagement',
    'TikTok shop',
];

function TikTokBrandPage({ brandId, controller }) {
    /* Off the geo reducer, not `useCurrentCountry`, which lags by a render. */
    const country = useSelector(({ geo }) => geo);
    const page = useTikTokEntity({
        entityType: 'brands',
        entityId: brandId,
        country,
        controller,
    });
    const { name, entity, row } = page;

    const healthStats = getTikTokAndSearchHealthStats({
        healthStatKeys: HEALTH_STAT_KEYS,
        tikTokDataPoint: row,
        price: entity?.price,
    });

    useTrackBrandView(DATA_SOURCES.TIKTOK, {
        brandId,
        brandName: name,
        countryName: country?.name || null,
    });

    return (
        <EntityPage
            page={page}
            title={{ name, isLoading: page.entityQuery.isLoading }}
            actions={
                <ToggleWatch id={brandId} data={{ name, type: 'brand' }} />
            }
            space="1.5rem"
            sections={
                <>
                    <EntityTopTerms as={TopHashtagsSection} highlighted />
                    <EntityTopPosts as={TopPostsSection} />
                </>
            }
            tabs={
                <EntityTabs>
                    <EntityTab name="related categories">
                        <EntityRelatedTable
                            as={RelatedCategoriesSectionTable}
                            subject="categories"
                            icon="affinity"
                            title="Related Categories"
                            exportType="related-hashtag"
                            exportEntity={name || 'brand'}
                            enabled={!!(brandId && country)}
                        />
                    </EntityTab>
                    <EntityTab name="creators">
                        <EntityCreators exportEntity={name || 'trend'} />
                    </EntityTab>
                </EntityTabs>
            }
        >
            <div className="flex flex-col gap-3">
                <EntityHeroCard healthStats={healthStats} />
                <TikTokChartCard
                    metricList={METRICS}
                    healthStats={healthStats}
                />
            </div>
        </EntityPage>
    );
}

TikTokBrandPage.displayName = 'TikTokBrand';

export default TikTokBrandPage;
